import { useState, useEffect, useMemo } from 'react';

import {
  TableContainer as MuiTableContainer,
  Table as MuiTable,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  TableBody as MuiTableBody,
  TableFooter as MuiTableFooter,
  TablePagination as MuiTablePagination,
  Paper as MuiPaper,
  Typography as MuiTypography,
  SxProps,
  Theme,
} from '@mui/material';

import { camelCaseToUserText, getColor } from '@cw/utils';

import { ITableProps, TableColumn } from './models';
import { DataTableRow } from './DataTableRow';
import { NoDataTableRow } from './NoDataTableRow';
import { InformationalWrapper } from '../InformationalWrapper';
import { InformationalHeader } from '../InformationalHeader';

const tablePageSizes: number[] = [10, 25, 50];

export function Table<T>(props: ITableProps<T>) {
  const {
    data,
    columns,
    keyAttribute,
    getRowClickRoute,
    onRowClick,
    addWrapperComponent,
    addPagination,
  } = props;

  const [currentPageSize, setCurrentPageSize] = useState(tablePageSizes[0]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pagedData, setPagedData] = useState<T[]>([]);

  const visibleColumns = useMemo(() => columns.filter(x => !x.hidden), [columns]);

  const getColumnLabel = (column: TableColumn<T>): string => {
    return column.label || camelCaseToUserText(column.key as string);
  };

  useEffect(() => {
    let startIndex: number;
    let endIndex: number;
    let newPageIndex = currentPageIndex;
    do {
      startIndex = currentPageSize * newPageIndex;
      endIndex = Math.min(startIndex + currentPageSize, data.length);

      if (data.length > 0 && startIndex >= data.length) {
        newPageIndex--;
      }
    } while (data.length > 0 && startIndex >= data.length);

    setPagedData(data.slice(startIndex, endIndex));
    if (newPageIndex !== currentPageIndex) {
      setCurrentPageIndex(newPageIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageSize, currentPageIndex, data]);

  const TablePagination = ({ sx }: { sx?: SxProps<Theme> }) => (
    <MuiTablePagination
      rowsPerPageOptions={tablePageSizes}
      colSpan={visibleColumns.length}
      count={data.length}
      rowsPerPage={currentPageSize}
      page={currentPageIndex}
      onPageChange={(_, newPage: number) => setCurrentPageIndex(newPage)}
      onRowsPerPageChange={(e) =>
        setCurrentPageSize(parseInt(e.target.value, 10))
      }
      sx={sx}
    />
  );

  const tableRoot = (
    <MuiTable stickyHeader={true} sx={{ width: '100%' }}>
      <MuiTableHead
        sx={{
          '.MuiTableCell-stickyHeader': {
            background: 'var(--color-light)'
          },
          borderBottomColor: 'black',
        }}
      >
        <MuiTableRow>
          {visibleColumns.map((column, index) => (
            <MuiTableCell
              key={index}
              sx={{
                padding: '30px 16px',
                color: column.textColor
                  ? getColor(column.textColor)
                  : undefined,
              }}
            >
              <InformationalHeader variant='body2' fontWeight='bold' tooltip={column.columnHeaderInfoTooltip}>
                {getColumnLabel(column)}
              </InformationalHeader>
            </MuiTableCell>
          ))}
        </MuiTableRow>
      </MuiTableHead>

      <MuiTableBody>
        {data?.length > 0 ? (
          pagedData.map((row, index) => (
            <DataTableRow
              row={row}
              columns={visibleColumns}
              key={keyAttribute ? (row[keyAttribute] as any) : index}
              getRowClickRoute={getRowClickRoute}
              onRowClick={onRowClick}
            />
          ))
        ) : (
          <NoDataTableRow columnCount={visibleColumns.length} />
        )}
      </MuiTableBody>

      {addPagination && data?.length > 0 && (
        <MuiTableFooter>
          <MuiTableRow>
            <TablePagination />
          </MuiTableRow>
        </MuiTableFooter>
      )}
    </MuiTable>
  );

  if (!addWrapperComponent) {
    return tableRoot;
  }

  return (
    <MuiTableContainer
      component={MuiPaper}
      sx={{
        width: '100%',
      }}
    >
      {tableRoot}
    </MuiTableContainer>
  );
}
